// ============================================================================
// Quality Document Version Configuration
// ============================================================================
// Add new versions here when needed. The system will automatically handle
// date-based routing between versions.

export interface QualityDocumentVersionConfig {
  version: number;
  route: string; // Route path segment (e.g., "new", "new-v4")
  validFrom: Date; // Start date for this version (inclusive)
  validTo: Date | null; // End date for this version (exclusive), null = no end
}

// Version configuration - ordered from newest to oldest
export const QUALITY_DOCUMENT_VERSIONS: QualityDocumentVersionConfig[] = [
  {
    version: 6,
    route: "new",
    validFrom: new Date("2026-01-01"),
    validTo: null, // Current/latest version
  },
  {
    version: 4,
    route: "new-v4",
    validFrom: new Date("1900-01-01"), // Any date before 2026-01-01
    validTo: new Date("2026-01-01"),
  },
  // To add v7 in the future, just add:
  // {
  //   version: 7,
  //   route: "new",  // or "new-v7" if v6 becomes "new-v6"
  //   validFrom: new Date("2027-01-01"),
  //   validTo: null,
  // },
  // And update v6's validTo to new Date("2027-01-01")
];

/**
 * Get the version config for a given workfrom date
 */
export const getVersionForDate = (
  workfromDate: string | Date,
): QualityDocumentVersionConfig | null => {
  if (!workfromDate) return null;

  const date =
    typeof workfromDate === "string" ? new Date(workfromDate) : workfromDate;
  date.setHours(0, 0, 0, 0);

  for (const config of QUALITY_DOCUMENT_VERSIONS) {
    const validFrom = new Date(config.validFrom);
    validFrom.setHours(0, 0, 0, 0);

    const validTo = config.validTo ? new Date(config.validTo) : null;
    if (validTo) validTo.setHours(0, 0, 0, 0);

    const isAfterStart = date >= validFrom;
    const isBeforeEnd = validTo === null || date < validTo;

    if (isAfterStart && isBeforeEnd) {
      return config;
    }
  }
  return null;
};

/**
 * Get the current/latest version (the one with validTo = null)
 */
export const getCurrentVersion = (): QualityDocumentVersionConfig => {
  return (
    QUALITY_DOCUMENT_VERSIONS.find((v) => v.validTo === null) ||
    QUALITY_DOCUMENT_VERSIONS[0]
  );
};

/**
 * Check if a date requires a different version than the current page
 */
export const shouldRedirectToVersion = (
  workfromDate: string,
  currentVersion: number,
): QualityDocumentVersionConfig | null => {
  const targetVersion = getVersionForDate(workfromDate);
  if (targetVersion && targetVersion.version !== currentVersion) {
    return targetVersion;
  }
  return null;
};

// ============================================================================
// Project State
// ============================================================================

export const newProjectState = {
  isValid: false,
  formData: {
    name: {
      required: true,
      value: "",
      isTouched: false,
    },
    supervisor: {
      required: true,
      value: [],
      isTouched: false,
    },
    tiler: {
      required: false,
      value: [],
      isTouched: false,
    },
  },
};

export const editProjectState = (name: string) => {
  return {
    isValid: false,
    formData: {
      name: {
        required: true,
        value: name,
        isTouched: false,
      },
      supervisor: {
        required: true,
        value: [],
        isTouched: false,
      },
      tiler: {
        required: false,
        value: [],
        isTouched: false,
      },
      prevParticipants: [],
      companyPersons: [],
    },
  };
};

export const newQualityDocumentState = () => {
  return {
    supplier: {},
    suppliersystemfloor: {},
    suppliersystemwallzone1: {},
    suppliersystemwallzone2: {},
    persontiler: "",
    personsupervisor: "",
    isValid: false,
    formData: {
      authorizationno: {
        required: true,
        value: "",
      },
      city: {
        required: true,
        value: "",
      },
      clientname: {
        required: true,
        value: "",
      },
      company: {
        required: true,
        value: 0,
      },
      companyname: {
        required: true,
        value: "",
      },
      copytoauthorizedcompany: {
        required: false,
        value: false,
      },
      floorfoundation: {
        required: true,
        value: "empty",
      },
      floorslope: {
        required: false,
        value: undefined,
      },
      floorslopenotok: {
        required: false,
        value: undefined,
      },
      companysupplierfloor: {
        required: true,
        value: 0,
      },
      tresholdok: {
        required: false,
        value: false,
      },
      suppliersystemfloor: {
        required: true,
        value: 0,
      },
      newproduction: {
        required: true,
        value: false,
        relation: ["renovation"],
      },
      pipesealingok: {
        required: false,
        value: false,
      },
      sealingdoorwindow: {
        required: false,
        value: false,
      },
      objectname: {
        required: true,
        value: "",
      },
      otherinformation: {
        required: true,
        value: "",
      },
      discrepancies: {
        required: true,
        value: "",
      },
      otherleadthrough: {
        required: true,
        value: undefined,
      },
      personsupervisor: {
        required: true,
        value: 0,
      },
      persontiler: {
        required: true,
        value: 0,
      },
      picture: {
        required: false,
        value: [],
      },
      project: {
        required: true,
        value: 0,
      },
      registrationno: {
        required: true,
        value: "",
      },
      renovation: {
        required: true,
        value: false,
        relation: ["newproduction"],
      },
      responsibilityinsurance: {
        required: true,
        value: "",
      },
      sealingworkok: {
        required: false,
        value: undefined,
      },
      supplementtoclient: {
        required: false,
        value: false,
      },
      wallfoundation: {
        required: true,
        value: "empty",
      },
      companywallzone1supplier: {
        required: true,
        value: 0,
      },
      suppliersystemwallzone1: {
        required: true,
        value: 0,
      },
      companywallzone2supplier: {
        required: true,
        value: 0,
      },
      draintype: {
        required: true,
        value: "",
      },
      clamptype: {
        required: true,
        value: "",
      },
      suppliersystemwallzone2: {
        required: true,
        value: 0,
      },
      wellbyothercontractor: {
        required: false,
        value: false,
      },
      wellfixedmount: {
        required: false,
        value: false,
      },
      newfloordrain: {
        required: false,
        value: false,
      },
      welltypeapproved: {
        required: false,
        value: false,
      },
      wetroomamount: {
        required: true,
        value: "",
      },
      workfrom: {
        required: true,
        value: "",
      },
      workto: {
        required: true,
        value: "",
      },
      wetworkteamlead: {
        required: false,
        value: "",
      },
      tilername: {
        required: false,
        value: "",
      },
      floorsupplier: {
        required: false,
        value: "",
      },
      floorsystem: {
        required: false,
        value: "",
      },
      wallzone1supplier: {
        required: false,
        value: "",
      },
      wallzone1system: {
        required: false,
        value: "",
      },
      wallzone2supplier: {
        required: false,
        value: "",
      },
      wallzone2system: {
        required: false,
        value: "",
      },
      companywallzone1supplier_text: {
        required: false,
        value: "",
      },
      companywallzone2supplier_text: {
        required: false,
        value: "",
      },
      companysupplierfloor_text: {
        required: false,
        value: "",
      },
    },
  };
};

const QUALITY_DOCUMENT_DRAFT_KEY = "qualityDocumentDraft";

export const saveQualityDocumentDraft = (formData: any, projectId: string) => {
  try {
    // Filter out runtime data that shouldn't be saved (supplier lists, etc.)
    // Only save formData fields
    const draftData = {
      formData,
      projectId,
      timestamp: Date.now(),
    };
    localStorage.setItem(QUALITY_DOCUMENT_DRAFT_KEY, JSON.stringify(draftData));
  } catch (error) {
    console.error("Failed to save draft:", error);
  }
};

export const loadQualityDocumentDraft = (projectId: string): any | null => {
  try {
    const draftData = localStorage.getItem(QUALITY_DOCUMENT_DRAFT_KEY);
    if (!draftData) {
      return null;
    }

    const parsed = JSON.parse(draftData);
    // Only return draft if it's for the same project
    if (parsed.projectId === projectId) {
      return parsed.formData;
    }
    return null;
  } catch (error) {
    console.error("Failed to load draft:", error);
    return null;
  }
};

export const clearQualityDocumentDraft = () => {
  try {
    localStorage.removeItem(QUALITY_DOCUMENT_DRAFT_KEY);
  } catch (error) {
    console.error("Failed to clear draft:", error);
  }
};

// Fields that exist only in v5 (not in v4)
export const V5_ONLY_FIELDS = [
  "floorslopenotok",
  "tresholdok",
  "sealingdoorwindow",
  "pipesealingok",
  "discrepancies",
  "draintype",
  "clamptype",
];

/**
 * Maps form data from version 5 to version 4 format
 * Removes v5-specific fields
 */
export const mapFormDataToV4 = (formData: any): any => {
  const v4FormData: any = { ...formData };

  V5_ONLY_FIELDS.forEach((field) => {
    delete v4FormData[field];
  });

  delete v4FormData.newfloordrain;

  return v4FormData;
};

// Alias for backwards compatibility
export const mapFormDataToLegacy = mapFormDataToV4;

/**
 * Maps form data from version 4 (legacy) to version 5 format
 * Adds default values for v5-specific fields
 */
export const mapFormDataToV5 = (formData: any): any => {
  const v5FormData: any = { ...formData };

  // Add default values for v5-specific fields
  v5FormData.floorslopenotok = {
    required: false,
    value: undefined,
  };
  v5FormData.tresholdok = {
    required: false,
    value: false,
  };
  v5FormData.sealingdoorwindow = {
    required: false,
    value: false,
  };
  v5FormData.pipesealingok = {
    required: false,
    value: false,
  };
  v5FormData.discrepancies = {
    required: true,
    value: "",
  };
  v5FormData.draintype = {
    required: true,
    value: "",
  };
  v5FormData.clamptype = {
    required: true,
    value: "",
  };
  v5FormData.newfloordrain = {
    required: false,
    value: false,
  };

  return v5FormData;
};

export const newQualityDocumentStateV4 = () => {
  return {
    supplier: {},
    suppliersystemfloor: {},
    suppliersystemwallzone1: {},
    suppliersystemwallzone2: {},
    persontiler: "",
    personsupervisor: "",
    isValid: false,
    formData: {
      authorizationno: {
        required: true,
        value: "",
      },
      city: {
        required: true,
        value: "",
      },
      clientname: {
        required: true,
        value: "",
      },
      company: {
        required: true,
        value: 0,
      },
      companyname: {
        required: true,
        value: "",
      },
      copytoauthorizedcompany: {
        required: false,
        value: false,
      },
      floorfoundation: {
        required: true,
        value: "empty",
      },
      floorslope: {
        required: false,
        value: undefined,
      },
      companysupplierfloor: {
        required: true,
        value: 0,
      },
      suppliersystemfloor: {
        required: true,
        value: 0,
      },
      newproduction: {
        required: true,
        value: false,
        relation: ["renovation"],
      },
      objectname: {
        required: true,
        value: "",
      },
      otherinformation: {
        required: true,
        value: "",
      },
      otherleadthrough: {
        required: true,
        value: undefined,
      },
      personsupervisor: {
        required: true,
        value: 0,
      },
      persontiler: {
        required: true,
        value: 0,
      },
      picture: {
        required: false,
        value: [],
      },
      project: {
        required: true,
        value: 0,
      },
      registrationno: {
        required: true,
        value: "",
      },
      renovation: {
        required: true,
        value: false,
        relation: ["newproduction"],
      },
      responsibilityinsurance: {
        required: true,
        value: "",
      },
      sealingworkok: {
        required: false,
        value: undefined,
      },
      supplementtoclient: {
        required: false,
        value: false,
      },
      wallfoundation: {
        required: true,
        value: "empty",
      },
      companywallzone1supplier: {
        required: true,
        value: 0,
      },
      suppliersystemwallzone1: {
        required: true,
        value: 0,
      },
      companywallzone2supplier: {
        required: true,
        value: 0,
      },
      suppliersystemwallzone2: {
        required: true,
        value: 0,
      },
      wellbyothercontractor: {
        required: false,
        value: false,
      },
      wellfixedmount: {
        required: false,
        value: false,
      },
      newfloordrain: {
        required: false,
        value: false,
      },
      welltypeapproved: {
        required: false,
        value: false,
      },
      wetroomamount: {
        required: true,
        value: "",
      },
      workfrom: {
        required: true,
        value: "",
      },
      workto: {
        required: true,
        value: "",
      },
      wetworkteamlead: {
        required: false,
        value: "",
      },
      tilername: {
        required: false,
        value: "",
      },
      floorsupplier: {
        required: false,
        value: "",
      },
      floorsystem: {
        required: false,
        value: "",
      },
      wallzone1supplier: {
        required: false,
        value: "",
      },
      wallzone1system: {
        required: false,
        value: "",
      },
      wallzone2supplier: {
        required: false,
        value: "",
      },
      wallzone2system: {
        required: false,
        value: "",
      },
      companywallzone1supplier_text: {
        required: false,
        value: "",
      },
      companywallzone2supplier_text: {
        required: false,
        value: "",
      },
      companysupplierfloor_text: {
        required: false,
        value: "",
      },
    },
  };
};

// Alias for backwards compatibility
export const newQualityDocumentStateLegacy = newQualityDocumentStateV4;
